// MultiplayerScene.js（重构版）
import Phaser from 'phaser';
import DebugHelper from './modules/DebugHelper.js';
import MultiplayerUI from './modules/Multiplayer/MultiplayerUI.js';
import MultiplayerInputDialog from './modules/Multiplayer/MultiplayerInputDialog.js';
import MultiplayerRoomList from './modules/Multiplayer/MultiplayerRoomList.js';
import MultiplayerConnection from './modules/Multiplayer/MultiplayerConnection.js';
import ServerConfig from './modules/ServerConfig.js';
import GameStateStore from './modules/GameStateStore.js';

export default class MultiplayerScene extends Phaser.Scene {
    constructor() {
        super('MultiplayerScene');
    }
    
    init(data = {}) {
        // 初始化多人游戏场景
        this.serverUrl = ServerConfig.getServerUrl(); // 根据环境自动选择服务器地址
        this.roomId = '';
        
        // 优先使用传递的数据，然后是全局用户信息，最后是gameState
        this.userId = data.userId || window.userInfo?.userId || null;
        this.playerName = data.username || window.userInfo?.username || GameStateStore.getPlayer()?.name || '玩家';
        
        // 初始化各个子模块
        this.ui = new MultiplayerUI(this);
        this.inputDialog = new MultiplayerInputDialog(this);
        this.roomList = new MultiplayerRoomList(this, this.ui);
        this.connection = new MultiplayerConnection(this, this.ui, this.roomList);
        
        // 如果用户已登录，更新连接器的用户信息
        if (this.userId) {
            this.connection.userId = this.userId;
        }
    }
    
    create() {
        // 初始化UI
        this.ui.create({
            playerName: this.playerName,
            serverUrl: this.serverUrl
        });
        
        // 设置输入字段编辑回调
        Object.keys(this.ui.inputFields).forEach(key => {
            const field = this.ui.inputFields[key];
            if (field.text) {
                field.text.off('pointerdown');
                field.text.on('pointerdown', () => {
                    this.inputDialog.showInputDialog(
                        key,
                        field.label.text,
                        field.value,
                        (value) => {
                            this.ui.updateInputField(key, value);
                            // 更新对应的变量
                            if (key === 'playerName') {
                                this.playerName = value;
                            } else if (key === 'serverUrl') {
                                this.serverUrl = value;
                            }
                        }
                    );
                });
            }
        });
        
        // 设置连接按钮回调
        this.ui.setCreateRoomButtonCallback(() => {
            this.showCreateRoomDialog();
        });
        
        this.ui.setRefreshButtonCallback(() => {
            this.connection.refreshRoomList();
        });
        
        // 设置返回按钮回调（createBackButton 内部会创建按钮）
        this.ui.createBackButton(() => this.goBack());
        
        // 设置连接按钮回调
        this.ui.setConnectButtonCallback(() => {
            this.connectToServer();
        });
        
        // 设置键盘控制
        this.setupControls();
    }
    
    setupControls() {
        this.escapeKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
        this.escapeKey.on('down', () => {
            this.goBack();
        });
        
        this.enterKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ENTER);
        this.enterKey.on('down', () => {
            if (this.connection.getConnectionStatus() === 'disconnected') {
                this.connectToServer();
            }
        });
    }
    
    connectToServer() {
        this.connection.connectToServer(
            () => {
                // 连接成功
                this.roomList.showRoomListDialog();
                this.connection.refreshRoomList();
            },
            (error) => {
                // 连接失败
                DebugHelper.error('连接服务器失败', error);
            },
            (rooms) => {
                // 房间列表更新
                this.roomList.updateRoomList(rooms, (roomId) => this.joinRoom(roomId));
            },
            (roomId, roomInfo) => {
                // 房间创建成功
                this.roomList.hideRoomListDialog();
                this.joinRoom(roomId);
            },
            (payload) => {
                // 加入房间成功
                // 标记正在切换场景，防止 UI 更新
                this.connection.isTransitioning = true;
                this.time.delayedCall(1000, () => {
                    this.enterMultiplayerWorld();
                });
            }
        );
    }
    
    showCreateRoomDialog() {
        this.inputDialog.showCreateRoomDialog(
            `${this.playerName}的房间`,
            (roomName) => {
                this.connection.createRoom(roomName, 10);
            }
        );
    }
    
    joinRoom(roomId) {
        this.connection.joinRoom(roomId);
    }
    
    enterMultiplayerWorld() {
        // 保存多人游戏配置
        if (GameStateStore.getState()) {
            if (GameStateStore.getPlayer() && this.connection.playerName) {
                GameStateStore.patchPlayer({ name: this.connection.playerName });
            }

            GameStateStore.setMultiplayer({
                connected: true,
                serverUrl: this.connection.serverUrl,
                roomId: this.connection.roomId,
                playerName: this.connection.playerName,
                networkManager: this.connection.getNetworkManager()
            });
        }
        
        // 将网络管理器保存到全局，以便世界场景使用
        window.networkManager = this.connection.getNetworkManager();
        
        // 跳转到多人世界场景
        this.scene.start('WorldScene', {
            multiplayer: true,
            serverUrl: this.connection.serverUrl,
            roomId: this.connection.roomId,
            playerName: this.connection.playerName,
            networkManager: this.connection.getNetworkManager()
        });
    }
    
    goBack() {
        // 如果已连接，先断开连接
        this.connection.disconnect();
        
        // 清理房间列表UI
        this.roomList.destroy();
        
        // 返回标题场景
        this.scene.start('TitleScene');
    }
    
    update() {
        // 更新场景逻辑
    }
}
